'use client';

import { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

import {
  type BusinessCard,
  type TarjetaRed,
} from '@/components/hooks/useBusinessCards';
import {
  zigzagPalettes,
  ZigZagColorPicker,
  DesignZigZagHex,
} from '@/components/pages/company/invitationDesigns';
import { useRedesSociales } from '@/components/hooks/useRedesSociales';
import { SocialIcon } from '@/components/types/SocialIcon';

/* =============================================================================
   TIPOS
============================================================================= */

type EmpresaInfo = {
  empresa: string;
  logo: string | null;
  descripcion: string | null;
  descripcion_corta: string | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  ciudad: string | null;
  categoria_slug: string;
  ciudad_slug: string;
  empresa_slug: string;
  color_primario: string | null;
  color_secundario: string | null;
};

interface Props {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  id_empresa: string;
  empresaInfo: EmpresaInfo;
  card?: BusinessCard | null;
  qrCitasUrl?: string;
  qrCatalogoUrl?: string;
  reservasOn?: boolean;
  saving?: boolean;
  insertCard: (
    card: Omit<
      BusinessCard,
      | 'id_tarjeta'
      | 'nombre_empresa'
      | 'logo_empresa'
      | 'qr_citas_url'
      | 'qr_catalogo_url'
    >
  ) => Promise<{ ok: boolean }>;
  updateCard: (
    id_tarjeta: string,
    card: Partial<
      Omit<
        BusinessCard,
        | 'id_tarjeta'
        | 'nombre_empresa'
        | 'logo_empresa'
        | 'qr_citas_url'
        | 'qr_catalogo_url'
      >
    >
  ) => Promise<{ ok: boolean }>;
}

const EMPTY_CARD = (id_empresa: string): BusinessCard => ({
  id_empresa,
  nombre: '',
  puesto: '',
  email: '',
  telefono: '',
  direccion: '',
  descripcion: '',
  mostrar_qr_citas: true,
  mostrar_qr_catalogo: false,
  paleta: 'midnight',
  redes: [],
});

const EMPTY_RED = (): Pick<TarjetaRed, 'id_tipo_red' | 'url'> => ({
  id_tipo_red: '',
  url: '',
});

/* =============================================================================
   BOTTOM SHEET (móvil) / DIÁLOGO CENTRADO (PC)
============================================================================= */

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-2xl sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
`;

/* =============================================================================
   CAMPO HELPER
============================================================================= */

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
      {label}
    </label>
    {children}
  </div>
);

/* =============================================================================
   ACORDEÓN DE RED INDIVIDUAL
============================================================================= */

function AccordionRed({
  red,
  onUrlChange,
  onRemove,
}: {
  red: TarjetaRed;
  onUrlChange: (url: string) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-white/10 overflow-hidden">
      {/* Cabecera siempre visible */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors text-left"
      >
        <SocialIcon
          network={red.nombre}
          size={16}
          color="currentColor"
          style={{ opacity: 0.75 }}
        />
        <span className="font-medium text-gray-700 dark:text-gray-200 shrink-0">
          {red.nombre}
        </span>
        <span className="text-gray-400 dark:text-gray-500 text-xs truncate flex-1">
          {red.url}
        </span>
        <span
          className={`shrink-0 text-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        >
          ▾
        </span>
      </button>

      {/* Panel desplegable */}
      {open && (
        <div className="px-3 pb-3 pt-2 bg-gray-50 dark:bg-white/[0.03] border-t border-gray-100 dark:border-white/10 space-y-2">
          <label className="text-xs text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wide">
            URL o usuario
          </label>
          <div className="flex gap-2">
            <Input
              value={red.url}
              onChange={(e) => onUrlChange(e.target.value)}
              placeholder="https://..."
              className="flex-1 h-9 text-sm border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="shrink-0 h-9 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   COMPONENTE PRINCIPAL
============================================================================= */

export default function BusinessCardDialog({
  open,
  onClose,
  id_empresa,
  empresaInfo,
  card,
  qrCitasUrl,
  qrCatalogoUrl,
  reservasOn = false,
  saving = false,
  insertCard,
  updateCard,
}: Props) {
  const { tiposRed, loading: loadingTipos } = useRedesSociales(
    id_empresa,
    open
  );

  const isEdit = !!card?.id_tarjeta;

  const [form, setForm] = useState<BusinessCard>(
    card ?? EMPTY_CARD(id_empresa)
  );
  const [newRed, setNewRed] = useState(EMPTY_RED());

  useEffect(() => {
    setForm(card ?? EMPTY_CARD(id_empresa));
    setNewRed(EMPTY_RED());
  }, [card, id_empresa, open]);

  /* ── helpers ── */

  const set = (key: keyof BusinessCard, value: unknown) =>
    setForm((f) => ({ ...f, [key]: value }));

  const addRed = () => {
    if (!newRed.id_tipo_red || !newRed.url.trim()) return;

    const tipo = tiposRed.find((t) => t.id_tipo_red === newRed.id_tipo_red);
    if (!tipo) return;

    const yaExiste = (form.redes ?? []).some(
      (r) => r.id_tipo_red === newRed.id_tipo_red
    );
    if (yaExiste) return;

    const red: TarjetaRed = {
      id_tipo_red: tipo.id_tipo_red,
      nombre: tipo.nombre,
      icono: tipo.icono ?? undefined,
      url: newRed.url.trim(),
    };

    setForm((f) => ({ ...f, redes: [...(f.redes ?? []), red] }));
    setNewRed(EMPTY_RED());
  };

  const removeRed = (idx: number) =>
    setForm((f) => ({
      ...f,
      redes: (f.redes ?? []).filter((_, i) => i !== idx),
    }));

  const handleSave = async () => {
    const {
      id_tarjeta,
      nombre_empresa,
      logo_empresa,
      qr_citas_url,
      qr_catalogo_url,
      ...cleanForm
    } = form;
    const resp = isEdit
      ? await updateCard(card!.id_tarjeta!, cleanForm)
      : await insertCard(cleanForm);
    if (resp?.ok) onClose(true);
  };

  /* ── paleta activa ── */
  const activePalette =
    zigzagPalettes.find((p) => p.id === form.paleta) ?? zigzagPalettes[0];

  /* ── preview ── */
  const empresaPreview = {
    empresa: empresaInfo.empresa,
    logo: empresaInfo.logo,
    descripcion_corta: form.descripcion || form.puesto || '',
    telefono: form.telefono || '',
    email: form.email || '',
    direccion: form.direccion || '',
  };

  /* ── tipos disponibles (excluir los ya añadidos) ── */
  const tiposDisponibles = tiposRed.filter(
    (t) => !(form.redes ?? []).some((r) => r.id_tipo_red === t.id_tipo_red)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={SHEET_CLASS}>
        {/* Header fijo */}
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {isEdit ? 'Editar tarjeta' : 'Nueva tarjeta'}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              {isEdit
                ? 'Modifica los datos de la tarjeta de presentación.'
                : 'Crea una nueva tarjeta de presentación para tu empresa o equipo.'}
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={() => onClose()}
            className="absolute top-4 right-4 sm:top-5 sm:right-6 p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 space-y-5 pb-4">
          {/* ── Datos personales ── */}
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              Datos personales
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nombre *">
                <Input
                  value={form.nombre}
                  onChange={(e) => set('nombre', e.target.value)}
                  placeholder="Ej. María García"
                  className="border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
                />
              </Field>
              <Field label="Puesto">
                <Input
                  value={form.puesto ?? ''}
                  onChange={(e) => set('puesto', e.target.value)}
                  placeholder="Ej. Directora Comercial"
                  className="border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="maria@empresa.com"
                  className="border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
                />
              </Field>
              <Field label="Teléfono">
                <Input
                  value={form.telefono ?? ''}
                  onChange={(e) => set('telefono', e.target.value)}
                  placeholder="+34 600 000 000"
                  className="border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
                />
              </Field>
            </div>

            <Field label="Dirección">
              <Input
                value={form.direccion ?? ''}
                onChange={(e) => set('direccion', e.target.value)}
                placeholder="Calle Mayor 1, Madrid"
                className="border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
              />
            </Field>

            <Field label="Descripción corta">
              <Input
                value={form.descripcion ?? ''}
                onChange={(e) => set('descripcion', e.target.value)}
                placeholder="Breve descripción o eslogan"
                maxLength={100}
                className="border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
              />
            </Field>
          </div>

          {/* ── QR ── */}
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              Código QR
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Solo se mostrará un QR en la tarjeta.
            </p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Mostrar QR de citas
              </span>
              <Switch
                checked={form.mostrar_qr_citas}
                onCheckedChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    mostrar_qr_citas: v,
                    mostrar_qr_catalogo: v ? false : f.mostrar_qr_catalogo,
                  }))
                }
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Mostrar QR de catálogo
              </span>
              <Switch
                checked={form.mostrar_qr_catalogo}
                onCheckedChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    mostrar_qr_catalogo: v,
                    mostrar_qr_citas: v ? false : f.mostrar_qr_citas,
                  }))
                }
              />
            </div>
          </div>

          {/* ── Redes ── */}
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              Redes y enlaces
            </p>

            {/* Lista de redes ya añadidas — acordeón */}
            <div className="space-y-2">
              {(form.redes ?? []).map((r, idx) => (
                <AccordionRed
                  key={r.id_tipo_red}
                  red={r}
                  onUrlChange={(url) =>
                    setForm((f) => ({
                      ...f,
                      redes: (f.redes ?? []).map((x, i) =>
                        i === idx ? { ...x, url } : x
                      ),
                    }))
                  }
                  onRemove={() => removeRed(idx)}
                />
              ))}
            </div>

            {/* Añadir nueva red — inline mejorado */}
            {tiposDisponibles.length > 0 && (
              <div className="mt-1 rounded-lg border border-dashed border-gray-300 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-3">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 font-medium">
                  Añadir red
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={newRed.id_tipo_red}
                    onValueChange={(v) =>
                      setNewRed((r) => ({ ...r, id_tipo_red: v }))
                    }
                    disabled={loadingTipos}
                  >
                    <SelectTrigger className="w-full sm:w-44 shrink-0 h-11 py-0 border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-sm focus:ring-0 focus:ring-offset-0">
                      <SelectValue placeholder="Red social" />
                    </SelectTrigger>
                    <SelectContent
                      className="z-[70]"
                      position="popper"
                      sideOffset={4}
                      onCloseAutoFocus={(e: Event) => e.preventDefault()}
                    >
                      {tiposDisponibles.map((t) => (
                        <SelectItem key={t.id_tipo_red} value={t.id_tipo_red}>
                          <span className="flex items-center gap-2">
                            <SocialIcon
                              network={t.nombre}
                              size={14}
                              color="currentColor"
                            />
                            <span>{t.nombre}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex flex-1 min-w-0 gap-2">
                    <Input
                      value={newRed.url}
                      onChange={(e) =>
                        setNewRed((r) => ({ ...r, url: e.target.value }))
                      }
                      onKeyDown={(e) => e.key === 'Enter' && addRed()}
                      placeholder="URL o usuario"
                      className="flex-1 h-11 border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-400 dark:focus-visible:border-white/20"
                    />
                    <Button
                      variant="default"
                      onClick={addRed}
                      disabled={!newRed.id_tipo_red || !newRed.url.trim()}
                      className="shrink-0 h-11 bg-[#103254] hover:bg-[#0d2742] text-white gap-1.5 px-3"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Añadir</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Paleta ── */}
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              Paleta de color
            </p>
            <ZigZagColorPicker
              value={activePalette}
              onChange={(p) => set('paleta', p.id)}
            />
          </div>

          {/* ── Preview ── */}
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-4 space-y-3 overflow-hidden">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              Vista previa
            </p>
            <DesignZigZagHex
              empresa={empresaPreview}
              persona={{
                nombre: form.nombre,
                puesto: form.puesto,
                email: form.email,
                telefono: form.telefono,
                direccion: form.direccion,
                descripcion: form.descripcion,
                redes: form.redes,
              }}
              qrCitasUrl={form.mostrar_qr_citas ? qrCitasUrl : undefined}
              qrCatalogoUrl={
                form.mostrar_qr_catalogo ? qrCatalogoUrl : undefined
              }
              reservasOn={reservasOn}
              palette={activePalette}
            />
          </div>
        </div>

        {/* Footer fijo */}
        <DialogFooter className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-white/10 shrink-0 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => onClose()}
            disabled={saving}
            className={DLG_BTN_SECONDARY}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.nombre.trim()}
            className={DLG_BTN_PRIMARY}
          >
            {saving
              ? 'Guardando...'
              : isEdit
              ? 'Guardar cambios'
              : 'Crear tarjeta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}